import type { ReactNode } from 'react';
import { isApiError, problemOf } from '../api/errors';
import { refreshEffective } from '../api/session';
import { useCapability, useSessionSnapshot } from '../lib/capability';
import { rbacRoleLabel } from '../lib/labels';
import { Icon, type IconName } from './Icon';
import { Mono } from './Mono';
import { ProblemPanel } from './ProblemPanel';
import { Chip } from './Chip';

export function SkeletonRows({ rows = 5, className = '' }: { rows?: number; className?: string }) {
  return (
    <div className={`space-y-2 ${className}`} data-testid="skeleton" aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-3">
          <span className="skeleton h-3 w-24" />
          <span className="skeleton h-3 flex-1" />
          <span className="skeleton h-3 w-16" />
        </div>
      ))}
    </div>
  );
}

export function LoadingBlock({ label = 'در حال بارگذاری…' }: { label?: string }) {
  return (
    <div className="p-panel" role="status" aria-live="polite" data-testid="loading">
      <p className="mb-3 text-body text-ink-dim">{label}</p>
      <SkeletonRows />
    </div>
  );
}

export interface EmptyStateProps {
  title: string;
  hint?: string;
  icon?: IconName;
  action?: ReactNode;
}

export function EmptyState({ title, hint, icon = 'search', action }: EmptyStateProps) {
  return (
    <div
      className="flex flex-col items-center justify-center gap-2 px-panel py-8 text-center"
      data-testid="empty-state"
    >
      <Icon name={icon} size={22} className="text-ink-faint" />
      <p className="text-ui font-medium text-ink">{title}</p>
      {hint ? <p className="max-w-prose text-body text-ink-faint">{hint}</p> : null}
      {action}
    </div>
  );
}

/** One component decides loading / error / empty / ready for every page. */
export interface QueryStateProps {
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  isEmpty?: boolean;
  emptyTitle?: string;
  emptyHint?: string;
  emptyIcon?: IconName;
  onRetry?: () => void;
  /** Heading for the error panel (what the operator attempted). */
  errorHeading?: string;
  loadingLabel?: string;
  children: ReactNode;
}

export function QueryState({
  isLoading,
  isError,
  error,
  isEmpty = false,
  emptyTitle = 'داده‌ای وجود ندارد',
  emptyHint,
  emptyIcon,
  onRetry,
  errorHeading,
  loadingLabel,
  children,
}: QueryStateProps) {
  if (isLoading) return <LoadingBlock label={loadingLabel} />;
  if (isError) {
    return (
      <div className="p-panel">
        <ErrorState error={error} heading={errorHeading} onRetry={onRetry} />
      </div>
    );
  }
  if (isEmpty) {
    return <EmptyState title={emptyTitle} hint={emptyHint} icon={emptyIcon} />;
  }
  return <>{children}</>;
}

export function ErrorState({
  error,
  heading,
  onRetry,
}: {
  error?: unknown;
  heading?: string;
  onRetry?: () => void;
}) {
  const problem = problemOf(error);
  const api = isApiError(error) ? error : null;
  return (
    <ProblemPanel
      heading={heading}
      problem={problem}
      kind={api?.kind ?? 'transport'}
      message={
        api
          ? api.message
          : error instanceof Error
            ? error.message
            : String(error ?? 'unknown error')
      }
      retryAfterSeconds={api?.retryAfterSeconds}
      rawBody={api?.rawBody}
      onRetry={onRetry}
    />
  );
}

/**
 * Honest "this page needs an endpoint that does not exist yet" panel — used by
 * every pending route shell and by pending tabs inside live pages. Never shows
 * fake numbers.
 */
export function PendingApiPanel({
  title = 'در انتظار API',
  refs,
  note,
}: {
  title?: string;
  refs: readonly string[];
  note?: string;
}) {
  return (
    <div
      className="panel border-dashed border-surface-strong bg-surface-raised p-panel"
      data-testid="pending-api"
    >
      <div className="flex items-center gap-2">
        <Icon name="clock" className="text-status-stale-ink" />
        <h3 className="text-ui font-semibold text-ink">{title}</h3>
      </div>
      {note ? <p className="mt-2 text-body text-ink-dim">{note}</p> : null}
      <ul className="mt-3 space-y-1.5">
        {refs.map((ref) => (
          <li key={ref} className="flex items-center gap-2">
            <Mono value={ref.split(' ')[0] ?? ref} />
            {ref.includes('✅') ? (
              <Chip tone="completed" label="موجود" dot={false} />
            ) : ref.includes('🔧') ? (
              <Chip tone="warning" label="در دست پیاده‌سازی" dot={false} />
            ) : (
              <Chip tone="neutral" label="برنامه‌ریزی‌شده" dot={false} />
            )}
          </li>
        ))}
      </ul>
      <p className="mt-3 text-caption text-ink-faint">
        مرجع: docs/API_COVERAGE_FA.md — کنسول فقط مصرف‌کنندهٔ این APIهاست و هیچ مسیر داخلی یا دادهٔ
        ساختگی نمایش نمی‌دهد.
      </p>
    </div>
  );
}

/** Placeholder for deferred live mechanisms (SSE stream, Grafana embeds). */
export function PlaceholderPanel({
  title,
  body,
  plannedIn,
  icon = 'external',
}: {
  title: string;
  body: string;
  plannedIn: string;
  icon?: IconName;
}) {
  return (
    <div
      className="rounded-panel border border-dashed border-surface-strong bg-surface-void p-panel"
      data-testid="placeholder"
    >
      <div className="flex items-center gap-2">
        <Icon name={icon} className="text-ink-faint" />
        <h3 className="text-ui font-semibold text-ink">{title}</h3>
        <Chip tone="neutral" label={plannedIn} dot={false} className="ms-auto" />
      </div>
      <p className="mt-2 text-body text-ink-dim">{body}</p>
    </div>
  );
}

/* ------------------------------------------------------------------------ access states (U-08-f) */

/**
 * The two access states `docs/RBAC_AND_CONSOLE_FA.md` §۹ names, and the only place the console
 * renders one.
 *
 * `level="none"` is a page-shaped refusal — §۹ refuses «حذف بی‌صدای صفحه», so the route stays
 * reachable and says why it is empty. `level="read"` is the read-only explanation that §۹ demands
 * instead of a disabled button with no reason: the operator still sees the data, and the sentence
 * says exactly what they may not do and which capability would let them.
 *
 * Both halves read the caller's identity off `GET /ops/v1/permissions/effective` (`useSessionSnapshot`)
 * — the mirror, never a rule. The console cannot know which role holds a capability the caller lacks:
 * `permissions/effective` publishes only the caller's own levels (§۶), and the per-role table lives
 * behind `GET /ops/v1/permissions`, which is `policy.matrix` (owner-only). So this panel names the
 * caller's role, names the four roles the server published, and points at the door that holds the
 * answer rather than copying a matrix into the browser.
 */
export function DeniedState({
  capability,
  level,
  hint,
}: {
  capability: string;
  level: 'none' | 'read';
  /** What this page would let the operator do with the level they are missing. */
  hint?: string;
}) {
  const snapshot = useSessionSnapshot();
  const role = snapshot.effective?.role ?? snapshot.identity?.role ?? null;
  const roles = snapshot.effective?.roles ?? [];
  const none = level === 'none';
  return (
    <div
      className={`panel p-panel ${none ? 'border-surface-strong bg-surface-raised' : 'border-dashed border-surface-strong bg-surface-void'}`}
      data-testid={none ? 'denied-state' : 'read-only-state'}
      data-capability={capability}
      data-level={level}
    >
      <div className="flex items-center gap-2">
        <Icon
          name={none ? 'shield' : 'info'}
          className={none ? 'text-status-stale-ink' : 'text-ink-faint'}
        />
        <h3 className="text-ui font-semibold text-ink">
          {none ? 'این صفحه با مجوز شما باز نمی‌شود' : 'نمایش فقط‌خواندنی'}
        </h3>
        <Chip
          tone={none ? 'warning' : 'neutral'}
          label={none ? 'بی‌مجوز' : 'فقط‌خواندنی'}
          title={level}
          dot={false}
          className="ms-auto"
        />
      </div>
      <p className="mt-2 text-body text-ink-dim">
        کلید <Mono value={capability} className="mx-1" />
        {none
          ? `به نقش ${rbacRoleLabel(role)} سطحی نداده است؛ این پاسخِ خودِ سرور است.`
          : 'را در سطح read دارید: همه‌چیز را می‌بینید، ولی کنترل‌های نوشتن این صفحه کار نمی‌کنند.'}
      </p>
      {hint ? <p className="mt-2 text-body text-ink">{hint}</p> : null}
      <p className="mt-3 text-caption text-ink-faint">
        سطوح از <Mono value="GET /ops/v1/permissions/effective" /> می‌آید؛ فهرست نقش‌های این
        استقرار: {roles.map((name) => rbacRoleLabel(name)).join('، ') || '—'}. اینکه کدام نقش این
        کلید را دارد را <Mono value="GET /ops/v1/permissions" /> (کلید{' '}
        <Mono value="policy.matrix" />، فقط مالک) منتشر می‌کند — کنسول ماتریس را کپی نمی‌کند.
      </p>
    </div>
  );
}

/**
 * «نمی‌دانم» — the fourth state U-08-f/D10 refuses to let collapse into «نداری».
 *
 * The mirror has not answered yet, so no verdict exists. This is shaped differently from
 * `DeniedState` on purpose (a `role="status"` block, not a refusal panel), and it offers the one
 * honest action available: re-read `GET /ops/v1/permissions/effective`. Rendering this as a denial
 * would tell an operator they lack a permission the server has not been asked about — the same
 * class of lie as putting a zero in a `null` cell (U-06-e).
 */
export function UnknownAccessState({ capability }: { capability: string }) {
  const snapshot = useSessionSnapshot();
  return (
    <div
      className="panel p-panel border-dashed border-surface-strong bg-surface-raised"
      role="status"
      aria-live="polite"
      data-testid="access-unknown"
      data-capability={capability}
    >
      <div className="flex items-center gap-2">
        <Icon name="clock" className="text-status-stale-ink" />
        <h3 className="text-ui font-semibold text-ink">سطح دسترسی هنوز خوانده نشده است</h3>
      </div>
      <p className="mt-2 text-body text-ink-dim">
        پاسخ <Mono value="GET /ops/v1/permissions/effective" /> برای کلید{' '}
        <Mono value={capability} className="mx-1" />
        هنوز نرسیده است. این «بی‌مجوزی» نیست و آن را از «نداری» جدا نگه می‌داریم.
      </p>
      <SkeletonRows className="mt-3" rows={2} />
      {snapshot.status === 'signed-in' ? (
        <button
          type="button"
          className="btn btn-ghost btn-sm mt-3"
          onClick={() => void refreshEffective().catch(() => undefined)}
          data-testid="access-unknown-retry"
        >
          <Icon name="refresh" size={14} />
          دوباره بپرس
        </button>
      ) : null}
    </div>
  );
}

/**
 * A page-level access wrapper (RBAC §۹): the page keeps its route, and the console renders the
 * state the server published for the page's own capability.
 *
 * `edit` renders the page unchanged; `read` renders the page with §۹'s visible read-only
 * explanation above it; `none` renders `DeniedState`; `unknown` renders `UnknownAccessState`. The
 * level comes from `useCapability`, i.e. from the mirror — this component contains no rule, and a
 * page that passes a capability nobody published is refused by `test/navigationCapabilities.test.ts`.
 */
export function AccessGate({
  capability,
  hint,
  children,
}: {
  capability: string;
  /** Page's own sentence: what the missing level would have allowed. */
  hint?: string;
  children: ReactNode;
}) {
  const level = useCapability(capability);
  if (level === 'none') return <DeniedState capability={capability} level="none" hint={hint} />;
  if (level === 'unknown') return <UnknownAccessState capability={capability} />;
  return (
    <div className="space-y-block" data-testid="access-gate" data-level={level}>
      {level === 'read' ? (
        <DeniedState
          capability={capability}
          level="read"
          hint={hint ?? 'کنترل‌های نوشتن این صفحه جای خود توضیح می‌گیرند.'}
        />
      ) : null}
      {children}
    </div>
  );
}

/**
 * One write affordance, mirrored against its own capability.
 *
 * §۹/§۸ (۶ و ۷) rule out both obvious shortcuts: a disabled button with no reason reads as a bug,
 * and a silently removed control reads as a page that does not offer the action at all. So the
 * control is replaced, in place, by the sentence that explains it: what the caller holds for that
 * key, and which key would have made it live.
 *
 * The three non-`edit` answers get three different sentences. `unknown` must not borrow the
 * `read` one: «سطح شما read است» is a claim about the server's answer, and the server has not given
 * one — the same class of lie as rendering `unknown` as a denial (D10, U-06-e).
 */
export function WriteSurface({
  capability,
  hint,
  children,
}: {
  capability: string;
  /** What this control would have done, in the operator's own words. */
  hint: string;
  children: ReactNode;
}) {
  const level = useCapability(capability);
  if (level === 'edit') return <>{children}</>;
  const reason =
    level === 'none'
      ? 'این کلید را ندارید'
      : level === 'read'
        ? 'سطح شما برای این کلید read است'
        : 'سطح این کلید هنوز از `permissions/effective` خوانده نشده — این «بی‌مجوزی» نیست';
  // `none` removes the control outright, so §۸(۷) (no silent removal) wants more than the
  // refusal: the standard's sentence shape here is «no access, and here is what you would
  // see». `DeniedState` is the page-level version of that and is NOT duplicated — this stays
  // the in-place control-level line, naming the key and the level the door needs, then handing
  // back `hint` as the capability the operator would have had. `read`/`unknown` keep their
  // three distinct sentences and simply append the same `hint`.
  const tail = level === 'none' ? `با سطح edit این کنترل اینجا این کار را می‌کردید: ${hint}` : hint;
  return (
    <div
      className="flex items-start gap-2 text-caption text-ink-dim"
      data-testid="write-readonly"
      data-capability={capability}
      data-level={level}
    >
      <Icon name="info" size={14} className="mt-0.5 shrink-0 text-ink-faint" />
      <span>
        این کنترل نوشتن است و {reason}؛ کلید <Mono value={capability} className="mx-1" />
        در سطح{' '}
        <span className="mono" dir="ltr">
          edit
        </span>{' '}
        لازم است. {tail}
      </span>
    </div>
  );
}
